import { useState, useEffect } from "react";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Building2, Users, CreditCard, AlertTriangle, TrendingUp, Database, CheckCircle, XCircle, SkipForward, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const runMigrations = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await axios.post(`${API}/admin/run-migrations`, {}, { headers });
      setMigrationResult(res.data);
      const { applied, skipped, failed } = res.data;
      if (failed.length > 0) {
        toast.error(`${failed.length} migración(es) fallaron`);
      } else if (applied.length > 0) {
        toast.success(`${applied.length} migración(es) aplicadas correctamente`);
      } else {
        toast.success("Todo actualizado — no había migraciones pendientes");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar migraciones");
    } finally {
      setMigrating(false);
    }
  };

  const repairCancellations = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const res = await axios.post(`${API}/admin/repair/cancellations-all-orgs`, {}, { headers });
      setRepairResult(res.data);
      toast.success(`Reparación iniciada para ${res.data.triggered} organización(es). Los datos se actualizarán en segundo plano.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar la reparación");
    } finally {
      setRepairing(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800" /></div>;
  if (!stats) return <p className="text-red-500">Error loading stats</p>;

  const cards = [
    { label: "Organizaciones", value: stats.total_organizations, icon: Building2, color: "text-blue-600 bg-blue-50" },
    { label: "Usuarios", value: stats.total_users, icon: Users, color: "text-green-600 bg-green-50" },
    { label: "Suscripciones activas", value: stats.subscriptions.active, icon: CreditCard, color: "text-emerald-600 bg-emerald-50" },
    { label: "MRR estimado", value: `${stats.mrr_estimate.toLocaleString("es-ES")}€`, icon: TrendingUp, color: "text-purple-600 bg-purple-50" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <h1 className="text-2xl font-semibold text-slate-900">Dashboard Admin</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <Card key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{c.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{c.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}>
                  <c.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Subscription breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Estado de suscripciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {Object.entries(stats.subscriptions).map(([key, val]) => (
              <div key={key} className="text-center">
                <p className="text-2xl font-bold text-slate-900">{val}</p>
                <p className="text-xs text-slate-500 capitalize mt-1">{key === "none" ? "Sin plan" : key.replace("_", " ")}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Plan distribution */}
      {Object.keys(stats.plan_distribution).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Distribución por plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              {Object.entries(stats.plan_distribution).map(([plan, count]) => (
                <div key={plan} className="text-center">
                  <p className="text-xl font-bold text-slate-900">{count}</p>
                  <p className="text-sm text-slate-500 capitalize">{plan}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats.subscriptions.past_due > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Pagos pendientes</p>
            <p className="text-sm text-amber-700">{stats.subscriptions.past_due} organizaciones con pago fallido</p>
          </div>
        </div>
      )}

      {/* System Tools */}
      <Card data-testid="system-tools-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-500" />
            <CardTitle className="text-base">Herramientas del sistema</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Migraciones de base de datos</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Crea los índices pendientes en MongoDB. Seguro ejecutar varias veces — las migraciones ya aplicadas se saltan automáticamente.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={runMigrations}
              disabled={migrating}
              className="shrink-0"
              data-testid="run-migrations-btn"
            >
              {migrating ? (
                <><span className="animate-spin mr-2">⟳</span>Ejecutando...</>
              ) : (
                <><Database className="w-3.5 h-3.5 mr-1.5" />Ejecutar migraciones</>
              )}
            </Button>
          </div>

          {migrationResult && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-xs" data-testid="migration-result">
              <div className="flex items-center gap-4">
                <span className="text-slate-500">Duración: <strong>{migrationResult.total_duration_s}s</strong></span>
                <span className={`font-medium ${migrationResult.status === "ok" ? "text-green-600" : "text-red-600"}`}>
                  {migrationResult.status === "ok" ? "Completado" : "Con errores"}
                </span>
              </div>
              {migrationResult.applied.length > 0 && (
                <div>
                  <p className="text-slate-500 mb-1 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Aplicadas ({migrationResult.applied.length})</p>
                  <ul className="space-y-0.5 ml-4">
                    {migrationResult.applied.map(v => <li key={v} className="text-green-700 font-mono">{v}</li>)}
                  </ul>
                </div>
              )}
              {migrationResult.skipped.length > 0 && (
                <div>
                  <p className="text-slate-500 mb-1 flex items-center gap-1"><SkipForward className="w-3 h-3 text-slate-400" /> Saltadas ({migrationResult.skipped.length})</p>
                  <ul className="space-y-0.5 ml-4">
                    {migrationResult.skipped.map(v => <li key={v} className="text-slate-400 font-mono">{v}</li>)}
                  </ul>
                </div>
              )}
              {migrationResult.failed.length > 0 && (
                <div>
                  <p className="text-slate-500 mb-1 flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> Fallidas ({migrationResult.failed.length})</p>
                  <ul className="space-y-0.5 ml-4">
                    {migrationResult.failed.map(f => <li key={f.version} className="text-red-600 font-mono">{f.version}: {f.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <hr className="border-slate-100" />

          {/* Data Repair Tools */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Reparar cancelaciones — todas las organizaciones</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Lanza un sync completo de pedidos (90 días) para cada organización con PrestaShop configurado.
                Reconstruye <code className="bg-slate-100 px-1 rounded">daily_returns_metrics</code> con datos correctos de cancelaciones.
                Se ejecuta en segundo plano — seguro ejecutar cuando sea necesario.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={repairCancellations}
              disabled={repairing}
              className="shrink-0 border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="repair-cancellations-btn"
            >
              {repairing ? (
                <><span className="animate-spin mr-2">⟳</span>Iniciando...</>
              ) : (
                <><Wrench className="w-3.5 h-3.5 mr-1.5" />Reparar cancelaciones</>
              )}
            </Button>
          </div>

          {repairResult && (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs" data-testid="repair-result">
              <p className="text-amber-700 font-medium">{repairResult.message}</p>
              {repairResult.skipped > 0 && (
                <p className="text-amber-500 mt-1">Omitidas (sin config completa): {repairResult.skipped}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
